import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { ModalComponent } from '../../../../shared/components/ui/modal/modal.component';
import { ButtonComponent } from '../../../../shared/components/ui/button/button.component';
import { TemplateService } from '../../service/template.service';
import { FormCreatePayload, FormType } from '../../models/form.model';
import { TemplateModel } from '../../models/template.model';

type CreationMode = 'new' | 'from-template';

interface FormTypeOption {
  value: FormType;
  label: string;
  description: string;
  icon: string;
}

@Component({
  selector: 'app-form-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ModalComponent, ButtonComponent],
  templateUrl: './form-modal.component.html',
})
export class FormModalComponent implements OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Input() loading = false;
  @Input() isTemplate = false;
  @Output() closeModal = new EventEmitter<void>();
  @Output() create = new EventEmitter<FormCreatePayload>();
  @Output() createFromTemplate = new EventEmitter<{ templateId: number; name: string; internalNotes?: string }>();

  private readonly destroy$ = new Subject<void>();

  mode: CreationMode = 'new';
  nameControl = new FormControl('', [Validators.required, Validators.minLength(2)]);
  titleControl = new FormControl(''); // inc25: título visible (opcional)
  descriptionControl = new FormControl('');
  selectedType: FormType = 'INLINE';

  templates: TemplateModel[] = [];
  templatesLoading = false;
  selectedTemplate: TemplateModel | null = null;

  readonly typeOptions: FormTypeOption[] = [
    {
      value: 'INLINE',
      label: 'Incrustado',
      description: 'Dentro del contenido de la página',
      icon: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6"><rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" fill="none"/><path stroke-linecap="round" d="M7 9h10M7 12h7"/></svg>`,
    },
    {
      value: 'POPUP',
      label: 'Pop-up',
      description: 'Ventana centrada sobre el contenido',
      icon: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6"><rect x="2" y="2" width="20" height="20" rx="2" stroke="currentColor" fill="none" stroke-opacity=".3"/><rect x="5" y="6" width="14" height="12" rx="2" stroke="currentColor" fill="none"/><path stroke-linecap="round" d="M9 10h6M9 13h4"/></svg>`,
    },
    {
      value: 'STICKY_BAR',
      label: 'Barra fija',
      description: 'Franja horizontal en la página',
      icon: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6"><rect x="2" y="3" width="20" height="18" rx="2" stroke="currentColor" fill="none" stroke-opacity=".3"/><rect x="2" y="3" width="20" height="6" rx="2" stroke="currentColor" fill="none"/><path stroke-linecap="round" d="M7 6h10"/></svg>`,
    },
    {
      value: 'SLIDE_IN',
      label: 'Deslizante',
      description: 'Panel desde la esquina de la pantalla',
      icon: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6"><rect x="2" y="2" width="20" height="20" rx="2" stroke="currentColor" fill="none" stroke-opacity=".3"/><rect x="13" y="7" width="9" height="13" rx="2" stroke="currentColor" fill="none"/><path stroke-linecap="round" d="M16 11h4M16 14h3"/></svg>`,
    },
  ];

  constructor(private templateService: TemplateService) {}

  ngOnChanges(): void {
    if (!this.isOpen) {
      this.reset();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setMode(mode: CreationMode): void {
    this.mode = mode;
    if (mode === 'from-template' && !this.templates.length && !this.templatesLoading) {
      this.loadTemplates();
    }
  }

  selectTemplate(template: TemplateModel): void {
    this.selectedTemplate = template;
    if (!this.nameControl.value?.trim()) {
      this.nameControl.setValue(template.name);
    }
  }

  onClose(): void {
    this.closeModal.emit();
  }

  onSubmit(): void {
    const name = this.nameControl.value?.trim();
    if (!name || this.loading) return;

    if (this.mode === 'from-template') {
      if (!this.selectedTemplate) return;
      this.createFromTemplate.emit({
        templateId: this.selectedTemplate.id,
        name,
        internalNotes: this.descriptionControl.value?.trim() || undefined,
      });
    } else {
      this.create.emit({
        name,
        title: this.titleControl.value?.trim() || undefined,
        internalNotes: this.descriptionControl.value?.trim() || undefined,
        formType: this.selectedType,
        isTemplate: this.isTemplate,
      });
    }
  }

  get canSubmit(): boolean {
    const hasName = !!this.nameControl.value?.trim();
    if (this.mode === 'from-template') return hasName && !!this.selectedTemplate;
    return hasName;
  }

  typeLabel(type: FormType): string {
    const labels: Record<FormType, string> = {
      INLINE: 'Incrustado', POPUP: 'Pop-up', STICKY_BAR: 'Barra fija', SLIDE_IN: 'Deslizante',
    };
    return labels[type] ?? type;
  }

  typeBadgeClass(type: FormType): string {
    const map: Record<FormType, string> = {
      INLINE: 'bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-400',
      POPUP: 'bg-purple-50 text-purple-700 dark:bg-purple-500/15 dark:text-purple-400',
      STICKY_BAR: 'bg-orange-50 text-orange-700 dark:bg-orange-500/15 dark:text-orange-400',
      SLIDE_IN: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400',
    };
    return map[type] ?? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
  }

  private loadTemplates(): void {
    this.templatesLoading = true;
    this.templateService.getTemplates()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: templates => { this.templates = templates; this.templatesLoading = false; },
        error: () => { this.templatesLoading = false; },
      });
  }

  private reset(): void {
    this.mode = 'new';
    this.nameControl.reset('');
    this.titleControl.reset('');
    this.descriptionControl.reset('');
    this.selectedType = 'INLINE';
    this.selectedTemplate = null;
    this.templates = [];
    this.templatesLoading = false;
  }
}
